import React, { useState } from 'react';
import './EventCard.css';
import { format } from 'date-fns';
import { FiChevronDown, FiChevronUp, FiCalendar, FiShare2 } from 'react-icons/fi';
import TicketLotteryModal from './TicketLotteryModal';
import { getAnalyticsEventDetails, trackEvent } from '../lib/analytics';

const EventCard = ({
  event,
  searchDate,
  isInterested,
  canSave = false,
  onToggleInterested,
  onLotteryEntered,
}) => {
  const [showInfo, setShowInfo] = useState(false);
  const [showLottery, setShowLottery] = useState(false);

  const venue = event._embedded?.venues?.[0] || {};
  const addressLine = venue?.address?.line1 || '';
  const cityState = [venue?.city?.name, venue?.state?.stateCode]
    .filter(Boolean)
    .join(', ');

  // Spotify
  const hasSpotify = event._embedded?.attractions?.[0].externalLinks?.spotify?.[0] || {};

  // Raw dates
  const rawStartDate = event.dates?.start?.dateTime
    ? new Date(event.dates.start.dateTime)
    : event.dates?.start?.localDate
      ? new Date(event.dates.start.localDate)
      : null;

  const endDate = event.dates?.end?.localDate ? new Date(event.dates.end.localDate) : null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Show time
  const formattedTime = rawStartDate ? format(rawStartDate, 'h:mm a') : null;

  const userSearchDate = searchDate
    ? new Date(
      Number(searchDate.slice(0, 4)),
      Number(searchDate.slice(5, 7)) - 1,
      Number(searchDate.slice(8, 10))
    )
    : today;

  const eventDate = rawStartDate && rawStartDate < userSearchDate ? userSearchDate : rawStartDate;
  const formattedDate = eventDate ? format(eventDate, "EEE. MMMM dd, yyyy") : 'Date TBD';

  const isOngoing = rawStartDate && rawStartDate < today && endDate && endDate >= today;

  const eventInfo = event.info || event.pleaseNote || "Click on 'Get Tickets' to view more info on this event.";
  const analyticsDetails = getAnalyticsEventDetails(event);

  const handleToggle = (e) => {
    if (!e.target.closest('.ticket-btn, .interested-btn, .calendar-btn, .share-btn, .lottery-btn, .lottery-withdraw-btn')) {
      const nextShowInfo = !showInfo;
      setShowInfo(nextShowInfo);
      trackEvent(nextShowInfo ? 'event_card_opened' : 'event_card_closed', analyticsDetails);
    }
  };

  const isFree = event?.priceRanges?.[0]?.min === 0;

  const getCalendarDates = () => {
    const start = event?.dates?.start?.dateTime
      ? new Date(event.dates.start.dateTime)
      : event?.dates?.start?.localDate
        ? new Date(`${event.dates.start.localDate}T19:00:00`)
        : null;
    const end = event?.dates?.end?.dateTime
      ? new Date(event.dates.end.dateTime)
      : event?.dates?.end?.localDate
        ? new Date(`${event.dates.end.localDate}T22:00:00`)
        : start
          ? new Date(start.getTime() + 2 * 60 * 60 * 1000)
          : null;
    return { start, end };
  };

  const formatCalendarDate = (date) => {
    if (!date) return '';
    const iso = date.toISOString().replace(/[-:]/g, '').split('.')[0];
    return `${iso}Z`;
  };

  const handleAddToCalendar = (e) => {
    e.stopPropagation();
    trackEvent('event_calendar_clicked', analyticsDetails);
    const { start, end } = getCalendarDates();
    if (!start) return;
    const dates = `${formatCalendarDate(start)}/${formatCalendarDate(end)}`;
    const details = eventInfo;
    const location = [venue?.name, addressLine].filter(Boolean).join(', ');
    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: event.name || 'Event',
      dates,
      details,
      location,
    });
    window.open(`https://calendar.google.com/calendar/render?${params.toString()}`, '_blank');
  };

  const handleShare = async (e) => {
    e.stopPropagation();
    trackEvent('event_share_clicked', analyticsDetails);
    const shareData = {
      title: event.name || 'Event',
      text: `${event.name || 'Event'}${venue?.name ? ` at ${venue.name}` : ''}`,
      url: event.url,
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        // user cancelled or share failed
      }
      return;
    }
    if (navigator.clipboard?.writeText && event.url) {
      try {
        await navigator.clipboard.writeText(event.url);
        alert('Share options not available here. Link copied to clipboard.');
      } catch (error) {
      }
    }
  };

  return (
    <div key={event.id} className="event-card" onClick={handleToggle}>
      {isFree && <div className="free-banner">FREE</div>}

      <div className="event-left">
        <img
          src={event.images?.find((image) => image.ratio === '16_9')?.url}
          alt={event.name}
          className="event-image"
        />
        <a
          href={event.url}
          target="_blank"
          rel="noopener noreferrer"
          className="ticket-btn"
          onClick={(e) => {
            e.stopPropagation();
            trackEvent('event_ticket_clicked', analyticsDetails);
          }}
        >
          <button>Get Tickets</button>
        </a>
        {canSave && (
          <button
            type="button"
            className={`interested-btn ${isInterested ? 'is-active' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              trackEvent(isInterested ? 'event_unsave_clicked' : 'event_save_clicked', analyticsDetails);
              onToggleInterested?.(event);
            }}
          >
            {isInterested ? 'Saved ✓' : 'Save'}
          </button>
        )}
        <div className="event-actions">
          <button type="button" className="calendar-btn" onClick={handleAddToCalendar}>
            <FiCalendar aria-hidden="true" />
            <span>Add to Calendar</span>
          </button>
          <button type="button" className="share-btn" onClick={handleShare}>
            <FiShare2 aria-hidden="true" />
            <span>Share</span>
          </button>
          {/* <button
            type="button"
            className={`lottery-btn ${lotteryEntered ? 'is-entered' : ''}`}
            disabled={lotteryEntered}
            onClick={(e) => {
              e.stopPropagation();
              setShowLottery(true);
            }}
          >
            {lotteryEntered ? 'Waitlist Joined' : 'Join the Waitlist'}
          </button>
          {lotteryEntered && (
            <button type="button" className="lottery-withdraw-btn" onClick={handleWithdrawLottery}>
              Leave Waitlist
            </button> */}
          {/* )} */}
        </div>
      </div>

      <div className="event-right">
        <h3 className="event-title">{event.name}</h3>
        <p className="event-date">
          {formattedDate}{formattedTime ? ` • ${formattedTime}` : ''}
        </p>
        {/* <div className="event-stats">
          <span className="event-stat-pill">Saved: {interestedCount}</span>
          <span className="event-stat-pill">Waitlist: {lotteryCount}</span>
        </div> */}
        {isOngoing && (
          <p className="event-ongoing">
            Ongoing – ends {format(endDate, 'MMM dd, yyyy')}
          </p>
        )}
        <p className="venue-name">{venue?.name || ''}</p>
        <p className="venue-address">{addressLine}</p>
        {cityState && <p className="venue-city-state">{cityState}</p>}

        {hasSpotify.url && (
          <iframe
            src={hasSpotify.url.replace('open.spotify.com/', 'open.spotify.com/embed/')}
            width="100%"
            height="200"
            style={{ maxWidth: '100%', borderRadius: '8px' }}
            frameBorder="0"
            allow="encrypted-media"
            allowTransparency="true"
            title="Spotify Preview"
          ></iframe>
        )}

        <button
          className="info-toggle"
          onClick={(e) => {
            e.stopPropagation();
            const nextShowInfo = !showInfo;
            setShowInfo(nextShowInfo);
            trackEvent(nextShowInfo ? 'event_info_opened' : 'event_info_closed', analyticsDetails);
          }}
        >
          {showInfo ? (
            <>
              Hide Info <FiChevronUp />
            </>
          ) : (
            <>
              Show Info <FiChevronDown />
            </>
          )}
        </button>

        {showInfo && <p className="event-info">{eventInfo}</p>}
      </div>
      <TicketLotteryModal
        open={showLottery}
        onClose={() => setShowLottery(false)}
        event={event}
        onEntered={() => onLotteryEntered?.(event?.id)}
      />
    </div>
  );
};

export default EventCard;
